import { Link } from 'react-router-dom';
import { MapPin, MessageCircle, Share2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { sanitizeHTML } from '@/shared/utils/html-sanitizer';
import {
  labelForClientSegment,
  labelForInvestableAssets,
  labelForServiceOffering,
} from '@/shared/constants/fpProfileOptions';
import { getAdvisorName } from './advisorProfileViewUtils';
import type { AdvisorProfileViewProfile } from './AdvisorProfileView.types';

type AdvisorProfileViewProps = {
  profile: AdvisorProfileViewProfile;
  showServiceProviderActions?: boolean;
  onReferAdvisor?: () => void;
};

type RichTextSectionProps = {
  title: string;
  content: string | null;
};

const RichTextSection = ({ title, content }: RichTextSectionProps) => {
  if (!content) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <div
        className="prose prose-sm max-w-none text-gray-700"
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
      />
    </section>
  );
};

type BadgeListSectionProps = {
  title: string;
  labels: string[];
};

// Feature 074: renders a structured multi-select field as a labeled list of badges.
const BadgeListSection = ({ title, labels }: BadgeListSectionProps) => {
  if (labels.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {labels.map((label) => (
          <Badge key={label} variant="secondary">
            {label}
          </Badge>
        ))}
      </div>
    </section>
  );
};

type LabeledValueSectionProps = {
  title: string;
  value: string | null;
};

// Feature 074: renders a single-select field as a labeled value.
const LabeledValueSection = ({ title, value }: LabeledValueSectionProps) => {
  if (!value) {
    return null;
  }

  return (
    <section className="space-y-1">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-700">{value}</p>
    </section>
  );
};

const formatAdvisorAddress = (profile: AdvisorProfileViewProfile): string[] => {
  const lines: string[] = [];
  if (profile.addressLine1?.trim()) lines.push(profile.addressLine1.trim());
  if (profile.addressLine2?.trim()) lines.push(profile.addressLine2.trim());
  const cityState = [profile.addressCity?.trim(), profile.addressState?.trim()]
    .filter((value): value is string => Boolean(value && value.length))
    .join(', ');
  const zip = profile.addressZip?.trim();
  const cityStateZip = [cityState, zip].filter(Boolean).join(' ').trim();
  if (cityStateZip) lines.push(cityStateZip);
  return lines;
};

export const AdvisorProfileView = ({
  profile,
  showServiceProviderActions = false,
  onReferAdvisor,
}: AdvisorProfileViewProps) => {
  const advisorName = getAdvisorName(profile);
  const addressLines = formatAdvisorAddress(profile);
  const displayName = advisorName || profile.companyName || 'Financial Advisor';
  const secondaryCompany = advisorName && profile.companyName ? profile.companyName : null;
  // Feature 074: structured fields. Service offerings list wins over legacy free text.
  const serviceOfferingLabels = (profile.serviceOfferingsList ?? []).map(labelForServiceOffering);
  const clientSegmentLabels = (profile.predominantClientSegments ?? []).map(labelForClientSegment);
  const investableAssetsLabel = profile.investableAssets
    ? labelForInvestableAssets(profile.investableAssets)
    : null;
  const hasAboutContent = Boolean(
    profile.advisorBiography ||
      profile.companyOverview ||
      profile.serviceOfferings ||
      serviceOfferingLabels.length > 0 ||
      clientSegmentLabels.length > 0 ||
      investableAssetsLabel ||
      profile.idealClientProfile ||
      profile.differentiatingFactors,
  );

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="relative h-96">
            {profile.user.profilePhoto ? (
              <img
                src={profile.user.profilePhoto}
                alt={`${advisorName} profile`}
                className="h-full w-full object-contain bg-gray-50"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#026857] to-[#378F94]">
                <span className="text-6xl font-bold text-white">{advisorName.charAt(0)}</span>
              </div>
            )}
          </div>

          <div className="p-8">
            <div className="mb-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="secondary">Financial Advisor</Badge>
              </div>

              <h1 className="text-3xl text-gray-900">{displayName}</h1>
              {secondaryCompany && (
                <p className="text-sm text-gray-600">{secondaryCompany}</p>
              )}

              {addressLines.length > 0 && (
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                    <address className="not-italic">
                      {addressLines.map((line, index) => (
                        <div key={`${index}-${line}`}>{line}</div>
                      ))}
                    </address>
                  </div>
                </div>
              )}
            </div>

            {showServiceProviderActions && (
              <div className="space-y-3">
                <Button asChild className="w-full bg-[#026857] hover:bg-[#025247]">
                  <Link to={`/messages?recipient=${profile.user.id}`}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Contact Advisor
                  </Link>
                </Button>
                <Button variant="outline" className="w-full" onClick={onReferAdvisor}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Refer Advisor
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {hasAboutContent && (
        <Card className="p-6">
          <h2 className="mb-4 text-xl text-gray-900">About {displayName}</h2>
          <div className="space-y-6">
            <RichTextSection title="Advisor Biography" content={profile.advisorBiography} />
            <RichTextSection title="Company Overview" content={profile.companyOverview} />
            {serviceOfferingLabels.length > 0 ? (
              <BadgeListSection title="Service Offerings" labels={serviceOfferingLabels} />
            ) : (
              <RichTextSection title="Service Offerings" content={profile.serviceOfferings} />
            )}
            <BadgeListSection
              title="Predominant Client Segments Served"
              labels={clientSegmentLabels}
            />
            <LabeledValueSection title="Investable Assets" value={investableAssetsLabel} />
            <RichTextSection title="Ideal Client Profile" content={profile.idealClientProfile} />
            <RichTextSection
              title="Differentiating Factors"
              content={profile.differentiatingFactors}
            />
          </div>
        </Card>
      )}
    </div>
  );
};
